import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import Cookies from "js-cookie";
import apiClient from "../utils/apiClient";
import config from "../utils/config";

interface SigninState {
  isSignedIn?: boolean;
  email?: string | null;
  password?: string | null;
  error?: string | null;
  response?: string | null;
}

const initialState: SigninState = {
  isSignedIn: false,
  email: null,
  password: null,
  error: null,
  response: null,
};

export const signinMember = createAsyncThunk(
  "signin/signin",
  async (signinData: SigninState, thunkAPI) => {
    const superAdminUrl = `${config.services.ums}/api/v1/super-admins/signin`;
    const labUserUrl = `${config.services.ums}/api/v1/lab-users/signin`;

    const extractErrorMessage = (error: unknown): string => {
      const axiosErr = error as AxiosError<{ message?: string }>;
      if (axiosErr?.response?.data?.message) return axiosErr.response.data.message;
      if (error instanceof Error && error.message) return error.message;
      return "Sign in failed";
    };

    try {
      // Try super-admin first (admin portal primary persona), then fallback to lab-user.
      let response = await apiClient
        .post(superAdminUrl, {
          email: signinData.email,
          password: signinData.password,
        })
        .catch(async () => {
          return apiClient.post(labUserUrl, {
            lab_branch_email: signinData.email,
            password: signinData.password,
          });
        });

      if (!response?.data?.data?.token) {
        return thunkAPI.rejectWithValue("Sign in succeeded but token was not returned");
      }

      return response.data;
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(extractErrorMessage(error));
    }
  }
);

const signinSlice = createSlice({
  name: "signin",
  initialState,
  reducers: {
    resetSigninState: (state) => {
      state.email = null;
      state.password = null;
      state.isSignedIn = false;
      state.error = null;
      state.response = null;
    },
    signOut: (state) => {
      Cookies.remove("token");
      state.isSignedIn = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signinMember.pending, (state) => {
        state.isSignedIn = false;
        state.error = null;
      })
      .addCase(signinMember.fulfilled, (state, action) => {
        state.isSignedIn = true;
        state.response = action.payload.data;
        Cookies.set("token", action.payload.data.token, {
          expires: 7,
          secure: window.location.protocol === 'https:',
          sameSite: 'Strict',
        });
      })
      .addCase(signinMember.rejected, (state, action) => {
        state.isSignedIn = false;
        state.error = action.payload as string;
      });
  },
});

export const { signOut, resetSigninState } = signinSlice.actions;

export default signinSlice.reducer;
